import { useCallback, useRef, useEffect } from 'react';
import {
  useProgramGeneratorProgressStore,
  ProgressItem,
} from 'src/store/programGeneratorProgressStore';
import { useSaveProgramProgress } from 'src/api/mutations/useSaveProgramProgress';
import debounce from 'lodash/debounce';

export function useStepProgress<T>(
  stepId: string,
  initialValue: T,
  toStore: (val: T) => ProgressItem[],
  fromStore: (items: ProgressItem[]) => T,
) {
  const store = useProgramGeneratorProgressStore();
  const { mutate: save } = useSaveProgramProgress();

  const toStoreRef = useRef(toStore);
  const fromStoreRef = useRef(fromStore);
  const initialValueRef = useRef(initialValue);
  const saveRef = useRef(save);

  useEffect(() => {
    toStoreRef.current = toStore;
    fromStoreRef.current = fromStore;
    initialValueRef.current = initialValue;
    saveRef.current = save;
  }, [toStore, fromStore, initialValue, save]);

  const debouncedSaveRef = useRef<any>(null);

  useEffect(() => {
    debouncedSaveRef.current = debounce((payload: any) => {
      saveRef.current(payload);
    }, 1000);
    return () => {
      debouncedSaveRef.current?.flush();
    };
  }, []);

  useEffect(() => {
    const state = useProgramGeneratorProgressStore.getState();
    if (state.progress[stepId] === undefined) {
      const data = toStoreRef.current(initialValueRef.current);
      state.updateStepProgress(stepId, data);
      // We no longer trigger a save to backend on mount to avoid spamming the backend
      // and creating premature draft programs. Saves will happen via handleNext.
    }
  }, [stepId]);

  const storedItems = store.progress[stepId];
  const value = storedItems !== undefined ? fromStore(storedItems) : initialValue;

  const setValue = useCallback(
    (newVal: T | ((prev: T) => T)) => {
      const state = useProgramGeneratorProgressStore.getState();
      const currentStoredItems = state.progress[stepId];
      const currentValue =
        currentStoredItems !== undefined
          ? fromStoreRef.current(currentStoredItems)
          : initialValueRef.current;

      let finalVal: T;
      if (typeof newVal === 'function') {
        finalVal = (newVal as (prev: T) => T)(currentValue);
      } else {
        finalVal = newVal;
      }

      const data = toStoreRef.current(finalVal);
      state.updateStepProgress(stepId, data);

      // Save to backend asynchronously with debounce
      if (state.programId || state.clientId) {
        debouncedSaveRef.current?.({
          program_id: state.programId,
          client_id: state.clientId,
          step_id: stepId,
          data,
        });
      }
    },
    [stepId],
  );

  return [value, setValue] as const;
}
